import React, { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { AnimatePresence, motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { TESTIMONIALS } from "@/data/content";

export const Testimonials = () => {
  const { t } = useLang();
  const [index, setIndex] = useState(0);
  const count = TESTIMONIALS.length;

  const next = useCallback(() => setIndex((i) => (i + 1) % count), [count]);
  const prev = () => setIndex((i) => (i - 1 + count) % count);

  useEffect(() => {
    const id = setInterval(next, 6500);
    return () => clearInterval(id);
  }, [next]);

  const active = TESTIMONIALS[index];

  return (
    <section id="testimonials" data-testid="testimonials-section" className="py-24 md:py-32 lg:py-40 bg-[#F3EFE7]">
      <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
        <Reveal><Overline className="mb-6">{t.testimonials.overline}</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#1A1A1A] mb-16">
            {t.testimonials.title}
          </h2>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="relative min-h-[260px] md:min-h-[220px] flex flex-col items-center justify-center">
            <Quote size={48} className="text-[#C5A880] mb-8 mx-auto" strokeWidth={1} />
            <AnimatePresence mode="wait">
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
                data-testid="testimonial-active"
              >
                <p className="font-display text-2xl sm:text-3xl lg:text-4xl font-light italic leading-snug text-[#1A1A1A] max-w-3xl mx-auto">
                  &ldquo;{active.quote}&rdquo;
                </p>
                <p className="mt-8 text-xs tracking-[0.25em] uppercase text-[#B0926A]">{active.author}</p>
              </motion.div>
            </AnimatePresence>
          </div>
        </Reveal>

        <div className="flex items-center justify-center gap-6 mt-12">
          <button data-testid="testimonial-prev" onClick={prev} aria-label="Previous"
            className="w-11 h-11 flex items-center justify-center border border-[#1A1A1A]/20 text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#FAF9F6] transition-colors">
            <ChevronLeft size={18} />
          </button>
          <div className="flex gap-2">
            {TESTIMONIALS.map((_, i) => (
              <button
                key={i}
                data-testid={`testimonial-dot-${i}`}
                onClick={() => setIndex(i)}
                aria-label={`Go to testimonial ${i + 1}`}
                className={`h-1.5 transition-all duration-300 ${i === index ? "w-8 bg-[#C5A880]" : "w-1.5 bg-[#1A1A1A]/25"}`}
              />
            ))}
          </div>
          <button data-testid="testimonial-next" onClick={next} aria-label="Next"
            className="w-11 h-11 flex items-center justify-center border border-[#1A1A1A]/20 text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#FAF9F6] transition-colors">
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
};
